import { UserArrowRightIcon } from "@eworkit/iris";
import { LazyUserAvatar } from "components-new";
import React, { FC, useContext } from "react";
import { useTranslation } from "react-i18next";

import { MenuItem, MenuSeparator, NestedMenu } from "@/components/Navigation";
import { Text } from "@/components/Text";
import { ConsultantProfileContext } from "@/components/User/ConsultantProfileContext";
import { refresh } from "@/data/session";
import { Flex } from "@/eworkitComponents/components";
import { SessionContext } from "@/infrastructure/Session";
import { router } from "@/views/router";

import { ContextDropdownModals } from "../Navigation/ContextDropdownModals";
import { useContextDropdown } from "../Navigation/useContextDropdown";
import { ContextDropdownProfile } from "../Topbar/components/ContextDropdownProfile";
import { ContextDropdownProfilesList } from "../Topbar/components/ContextDropdownProfilesList";
import { NavItem } from "../Topbar/components/NavItem";
import { RouteNavItem } from "../Topbar/components/RouteNavItem";
import { IContextDropdownProps } from "./ContextDropdown";

interface IContextDropdownDesktopProps extends IContextDropdownProps {}

export const ContextDropdownDesktop: FC<IContextDropdownDesktopProps> = () => {
  const Session = useContext(SessionContext);
  const ConsultantProfile = useContext(ConsultantProfileContext);

  const {
    currentProfile,
    currentCompany,
    handleCreate,
    connectedProfiles,
    personalProfiles,
    handleConnectedProfileClick,
    handlePersonalProfileClick,
    renderCompanyLogo,
    ...rest
  } = useContextDropdown();

  const { t } = useTranslation(["session", "public"]);

  if (!currentProfile) {
    return null;
  }

  const baseClass = "navigation";

  const handleRefresh = async () => {
    const refreshedSessionUser = await refresh();
    Session.update(refreshedSessionUser);
  };

  const userSettingsRoute = router.get("user.settings");
  const notificationSettingsRoute = router.get("notifications.settings");
  const myProfileRoute = router.get("consultant-profiles.detail", {
    consultantProfileId: ConsultantProfile.currentProfileId,
  });
  const helpCenterRoute = t(`public:LINKS.HELP-CENTER`);
  const logOutRoute = router.get("logout");

  const singleProfile = personalProfiles.length + connectedProfiles.length < 2;

  const DropdownTarget: FC<any> = ({ profile, company }) => {
    return (
      <div onClick={handleRefresh}>
        <LazyUserAvatar
          avatar={profile?.avatar}
          firstName={profile?.firstName}
          lastName={profile?.lastName}
          company={company}
        />
      </div>
    );
  };

  return (
    <div className={`${baseClass}__context-dropdown`}>
      <MenuItem
        as={NestedMenu}
        level={1}
        key="view"
        buttonContent={<DropdownTarget profile={currentProfile} company={currentCompany} />}
        className={`${baseClass}--nested`}>
        {currentCompany && (
          <Flex className={`${baseClass}__company-bar`} padding={["md", false, "md", "md"]}>
            <Flex direction="row" padding={[false, false, false, "xxs"]} innerMargin="sm" align="center">
              {currentCompany && renderCompanyLogo("xs", "lg")}
              {currentCompany?.name && (
                <Text size="lg" weight="bold" color="black" className="-word-break-word">
                  {currentCompany.name}
                </Text>
              )}
            </Flex>
          </Flex>
        )}
        <Flex padding={["md", false, false, false]}>
          <ContextDropdownProfile
            key={currentProfile.id}
            profile={currentProfile}
            showActiveIndicator={false}
            showNotificationsIndicator={false}
            showCompany={false}
          />
        </Flex>
        <RouteNavItem
          path={myProfileRoute.path}
          exact={false}
          variant="nested"
          icon="user"
          text={t("COMPONENTS.TOPBAR.BUTTON_MY_PROFILE")}
        />
        <RouteNavItem
          path={notificationSettingsRoute.path}
          exact={false}
          variant="nested"
          icon="bell"
          text={t("COMPONENTS.TOPBAR.BUTTON_NOTIFICATIONS_SETTINGS")}
        />

        <MenuSeparator />

        <div className={`${baseClass}__context-dropdown__profile-switcher`}>
          {singleProfile ? (
            <button onClick={handleCreate}>
              <NavItem variant="nested" icon="user_add" text={t("COMPONENTS.TOPBAR.CONTEXT_DROPDOWN.BUTTON_CREATE")} />
            </button>
          ) : (
            <MenuItem
              as={NestedMenu}
              level={2}
              key="view"
              size="sm"
              iconLeft={<UserArrowRightIcon />}
              buttonContent={t("COMPONENTS.TOPBAR.BUTTON_SWITCH_PROFILE")}
              className={`${baseClass}--nested--3rd`}>
              <ContextDropdownProfilesList profiles={personalProfiles} onProfileClick={handlePersonalProfileClick} />

              <ContextDropdownProfilesList
                profiles={connectedProfiles}
                onProfileClick={handleConnectedProfileClick}
                showNotificationsCounter
              />
              <MenuSeparator />
              <button onClick={handleCreate}>
                <NavItem
                  variant="nested"
                  icon="user_add"
                  text={t("COMPONENTS.TOPBAR.CONTEXT_DROPDOWN.BUTTON_CREATE")}
                />
              </button>
            </MenuItem>
          )}
        </div>
        <MenuSeparator />
        <RouteNavItem
          path={userSettingsRoute.path}
          exact={false}
          variant="nested"
          icon="settings"
          text={t("COMPONENTS.TOPBAR.BUTTON_ACCOUNT_SETTINGS")}
        />
        <RouteNavItem
          path={helpCenterRoute}
          exact={false}
          target="_blank"
          variant="nested"
          icon="help"
          text={t("COMPONENTS.TOPBAR.BUTTON_HELP_CENTER")}
        />
        <MenuSeparator />
        <RouteNavItem
          path={logOutRoute.path}
          exact={false}
          variant="nested"
          icon="logout"
          text={t("COMPONENTS.TOPBAR.CONTEXT_DROPDOWN.BUTTON_LOGOUT")}
        />
      </MenuItem>

      <ContextDropdownModals currentProfile={currentProfile} {...rest} />
    </div>
  );
};
